import { PageSize } from '@/constant';
import {
	GetWorkerProgramStatusListParams,
	IWorkerProgramStatus,
} from '@/services';
import { mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { formatWorkerProgramStatusList, RootState } from '..';
import { AuthActions } from '../auth/auth.redux';
import { WorkerProgramActions } from '../worker-program/worker-program.redux';
import {
	IPayloadDeleteWorkerProgramStatusRequest,
	IPayloadGetWorkerProgramStatusExcelListRequest,
	IPayloadGetWorkerProgramStatusListRequest,
	IPayloadGetWorkerProgramStatusListSuccess,
	IWorkerProgramStatusListDetailStore,
} from './worker-program-status.type';

const InitParams: GetWorkerProgramStatusListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
	name: null,
	programStartDate: null,
	programEndDate: null,
	registStartDate: null,
	registEndDate: null,
};

const initialState = {
	fetching: false,
	excelListFetching: false,
	workerProgramStatusList: [] as IWorkerProgramStatusListDetailStore[],
	total: 0,
	params: InitParams,
};

const workerProgramStatusSlice = createSlice({
	initialState,
	name: 'workerProgramStatus',
	reducers: {
		/**---- GET WORKER PROGRAM STATUS LIST START ---- */
		getWorkerProgramStatusListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramStatusListRequest>
		) => {
			state.fetching = true;
			state.params = action?.payload;
		},
		getWorkerProgramStatusListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramStatusListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.workerProgramStatusList = content;
			state.total = total;
		},
		getWorkerProgramStatusListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.workerProgramStatusList = [];
		},

		getWorkerProgramStatusExcelListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramStatusExcelListRequest>
		) => {
			state.excelListFetching = true;
		},
		getWorkerProgramStatusExcelListSuccess: (state) => {
			state.excelListFetching = false;
		},
		getWorkerProgramStatusExcelListFailure: (state) => {
			state.excelListFetching = false;
		},
		/**---- GET WORKER PROGRAM STATUS LIST END ---- */

		/**---- DELETE WORKER PROGRAM STATUS START ---- */
		deleteWorkerProgramStatusRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerProgramStatusRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerProgramStatusSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerProgramStatusFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER PROGRAM STATUS START ---- */
	},

	extraReducers(builder) {
		builder.addCase(
			WorkerProgramActions.updateWorkerProgramSuccess,
			(state, action) => {
				const { data } = action.payload;
				const workerProgramStatusList = [...state.workerProgramStatusList];
				state.workerProgramStatusList = formatWorkerProgramStatusList(
					mergeListItemUpdated<
						IWorkerProgramStatusListDetailStore,
						IWorkerProgramStatus
					>(workerProgramStatusList, data)
				);
			}
		);

		builder.addCase(AuthActions.logoutSuccess, (state) => {
			return initialState;
		});
	},
});

export const WorkerProgramStatusSelectors = {
	selectFetching: (state: RootState) => state.workerProgramStatus.fetching,
	selectParams: (state: RootState) => state.workerProgramStatus.params,
	selectList: (state: RootState) =>
		state.workerProgramStatus.workerProgramStatusList,
	selectTotal: (state: RootState) => state.workerProgramStatus.total,
};

export const WorkerProgramStatusActions = workerProgramStatusSlice.actions;
export const WorkerProgramStatusReducer = workerProgramStatusSlice.reducer;
